import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Avatar,
  Tooltip,
  Chip,
  Collapse,
  Skeleton,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsIcon from "@mui/icons-material/Notifications";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DownloadIcon from "@mui/icons-material/Download";
import { useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import VpnKeyRoundedIcon from "@mui/icons-material/VpnKeyRounded";
import { useNotifications } from "../../context/NotificationContext";
import { useToast } from "../../context/ToastContext";
import { downloadJobHistoryReport } from "../../services/JobHistoryService";
import LawyerSessionRequiredDialog from "./Dialog/LawyerSessionRequiredDialog";

const WORKSPACE_TASK_TYPES = [
  "workspace_task_created",
  "workspace_task_updated",
  "workspace_task_assigned",
  "workspace_task_status_changed",
];

function getNotificationLink(n) {
  if (WORKSPACE_TASK_TYPES.includes(n.data?.type) && n.data?.task_id) {
    return `/workspace/${n.data.task_id}`;
  }
  return null;
}

const NOTIFICATION_TYPE_LABELS = {
  workspace_task_created: "Tareas",
  workspace_task_updated: "Tareas",
  workspace_task_assigned: "Tareas",
  workspace_task_status_changed: "Tareas",
};

function getNotificationTypeLabel(n) {
  const rawType = n.data?.type;
  if (!rawType) return null;

  const type = String(rawType).toLowerCase();
  if (NOTIFICATION_TYPE_LABELS[type]) return NOTIFICATION_TYPE_LABELS[type];

  const hasJobHistory = Boolean(n.data?.job_history_id ?? n.data?.data?.job_history_id);
  if (hasJobHistory || type.includes("job")) return "PROCESO";
  if (type.includes("task") || type.includes("workspace") || type.includes("work")) {
    return "Tareas";
  }

  return type.replace(/_/g, " ").toUpperCase();
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const diff = Date.now() - d.getTime();
  const sec = Math.floor(diff / 1000);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);

  if (sec < 60) return `hace ${sec}s`;
  if (min < 60) return `hace ${min}m`;
  if (hr < 24) return `hace ${hr}h`;
  return `hace ${day}d`;
}

function startOfLocalDay(date) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  ).getTime();
}

function dayLabel(isoDateStr) {
  if (!isoDateStr) return "Sin fecha";

  const d = new Date(isoDateStr);
  const today = new Date();

  const d0 = startOfLocalDay(d);
  const t0 = startOfLocalDay(today);

  // ✅ NO round
  const diffDays = Math.floor((t0 - d0) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Hoy";
  if (diffDays === 1) return "Ayer";

  return d.toLocaleDateString("es-AR"); // opcional
}

function groupByDay(notifications) {
  const map = new Map();

  for (const n of notifications) {
    const d = new Date(n.created_at);
    const dayTs = startOfLocalDay(d); // clave numérica real

    const label = dayLabel(n.created_at);

    if (!map.has(dayTs)) map.set(dayTs, { label, items: [] });
    map.get(dayTs).items.push(n);
  }

  const grouped = Array.from(map.entries())
    .map(([dayTs, { label, items }]) => {
      // items ya vienen ordenados (o reordenás por created_at)
      const sorted = [...items].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
      return { dayTs, label, items: sorted };
    })
    // ✅ orden por dayTs desc => hoy primero
    .sort((a, b) => b.dayTs - a.dayTs);

  return grouped;
}

function SkeletonItem() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Box
      sx={{
        mb: 1,
        p: 1.25,
        borderRadius: 3,
        border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
        background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.86)",
      }}
    >
      <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
        <Skeleton
          variant="rounded"
          width={38}
          height={38}
          sx={{ borderRadius: 3 }}
        />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="85%" height={22} />
          <Skeleton variant="text" width="65%" height={18} />
          <Box sx={{ mt: 0.8 }}>
            <Skeleton
              variant="rounded"
              width={90}
              height={22}
              sx={{ borderRadius: 999 }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

const TYPE_LABEL = {
  skipped: { text: "No enviado", color: "error" },
  partial: { text: "Enviado incompleto", color: "warning" },
  failed:  { text: "Error al subir", color: "error" },
  session_required: { text: "Sesión pendiente", color: "warning" },
};

function WarningDetails({ details, isDark, onRequestSession }) {
  return (
    <Box
      sx={{
        mt: 1,
        borderRadius: 2,
        overflow: "hidden",
        border: isDark
          ? "1px solid rgba(237,108,2,0.25)"
          : "1px solid rgba(237,108,2,0.22)",
        background: isDark ? "rgba(237,108,2,0.06)" : "rgba(255,244,229,0.7)",
      }}
    >
      {details.map((item, i) => {
        const typeMeta = TYPE_LABEL[item.type] ?? { text: item.type ?? "—", color: "default" };
        return (
          <Box
            key={i}
            sx={{
              px: 1.2,
              py: 0.8,
              borderBottom:
                i < details.length - 1
                  ? isDark
                    ? "1px solid rgba(237,108,2,0.15)"
                    : "1px solid rgba(237,108,2,0.12)"
                  : "none",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mb: 0.4, flexWrap: "wrap" }}>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: "monospace",
                  fontWeight: 800,
                  fontSize: 12,
                  color: "warning.dark",
                  bgcolor: isDark ? "rgba(237,108,2,0.15)" : "rgba(237,108,2,0.12)",
                  px: 0.8,
                  py: 0.1,
                  borderRadius: 1,
                }}
              >
                {item.code}
              </Typography>
              <Chip
                label={typeMeta.text}
                size="small"
                color={typeMeta.color}
                variant="outlined"
                sx={{ height: 18, fontSize: 10, fontWeight: 800 }}
              />
            </Box>
            {(item.issues ?? []).map((issue, j) => (
              <Typography
                key={j}
                variant="caption"
                sx={{ display: "block", opacity: 0.8, lineHeight: 1.4, pl: 0.3 }}
              >
                • {issue}
              </Typography>
            ))}
            {item.type === "session_required" && item.lawyer && (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<VpnKeyRoundedIcon sx={{ fontSize: 15 }} />}
                onClick={() => onRequestSession?.(item.lawyer)}
                sx={{ mt: 0.7, borderRadius: 2, fontWeight: 800, textTransform: "none", fontSize: 12 }}
              >
                Pegar sesión
              </Button>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export default function NotificationsDrawer({ open, onClose }) {
  const { notifications, markAllAsRead, loading } = useNotifications();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [expandedId, setExpandedId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [sessionRequiredLawyer, setSessionRequiredLawyer] = useState(null);

  const toggleExpand = (id) =>
    setExpandedId((prev) => (prev === id ? null : id));

  const handleDownloadReport = async (jobHistoryId) => {
    setDownloadingId(jobHistoryId);
    try {
      const response = await downloadJobHistoryReport(jobHistoryId);
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `reporte_proceso_${jobHistoryId}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      showToast("No se pudo descargar el reporte completo.", "error");
    } finally {
      setDownloadingId(null);
    }
  };

  const APP_BAR_HEIGHT_DESKTOP = 72;
  const APP_BAR_HEIGHT_MOBILE = 72;
  const BOTTOM_NAV_HEIGHT_MOBILE = 72;

  const hasUnread = useMemo(
    () => (notifications || []).some((n) => !n.read_at),
    [notifications],
  );

  const grouped = useMemo(() => {
    const list = Array.isArray(notifications) ? notifications : [];
    // orden global desc por created_at
    const sorted = [...list].sort((a, b) => {
      const ta = new Date(a.created_at).getTime();
      const tb = new Date(b.created_at).getTime();
      return tb - ta;
    });
    return groupByDay(sorted);
  }, [notifications]);

  return (
    <>
      <LawyerSessionRequiredDialog
        open={Boolean(sessionRequiredLawyer)}
        onClose={() => setSessionRequiredLawyer(null)}
        lawyer={sessionRequiredLawyer}
      />

      <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{
        BackdropProps: {
          sx: {
            top: { xs: APP_BAR_HEIGHT_MOBILE, md: APP_BAR_HEIGHT_DESKTOP },
            backgroundColor: { xs: "transparent", md: "rgba(0,0,0,0.3)" },
          },
        },
      }}
      PaperProps={{
        sx: {
          top: { xs: APP_BAR_HEIGHT_MOBILE, md: APP_BAR_HEIGHT_DESKTOP },
          height: {
            xs: `calc(100% - ${APP_BAR_HEIGHT_MOBILE}px - ${BOTTOM_NAV_HEIGHT_MOBILE}px)`,
            md: `calc(100% - ${APP_BAR_HEIGHT_DESKTOP}px)`,
          },
          width: { xs: "100vw", sm: 390 },
          maxWidth: "100vw",

          // GLASS like your AppBar
          background: isDark ? "rgba(15,23,42,0.94)" : "rgba(255,255,255,0.91)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          borderLeft: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.10)",
          boxShadow: "none",
        },
      }}
    >
      {/* Header sticky */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          background: isDark ? "rgba(15,23,42,0.92)" : "rgba(255,255,255,0.86)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.10)",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.6,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 3,
                display: "grid",
                placeItems: "center",
                background: isDark ? "rgba(25,118,210,0.12)" : "rgba(255,255,255,0.86)",
                border: "1px solid rgba(25,118,210,0.22)",
                boxShadow: "0 10px 26px rgba(0,0,0,0.08)",
                color: "primary.main",
                "& svg": { fontSize: 22, display: "block" },
              }}
            >
              <NotificationsIcon />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{ fontWeight: 900, fontSize: 18, lineHeight: 1.1 }}
              >
                Notificaciones
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }} noWrap>
                {Array.isArray(notifications) ? notifications.length : 0} en
                total
                {hasUnread ? " · hay nuevas" : ""}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip
              title={hasUnread ? "Marcar todas como leídas" : "No hay nuevas"}
            >
              <span>
                <IconButton
                  size="small"
                  onClick={markAllAsRead}
                  disabled={!hasUnread || loading}
                  sx={{
                    borderRadius: 2,
                    bgcolor: "rgba(0,0,0,0.04)",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.07)" },
                  }}
                >
                  <DoneAllIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Cerrar">
              <IconButton
                onClick={onClose}
                sx={{
                  borderRadius: 2,
                  bgcolor: "rgba(0,0,0,0.04)",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.07)" },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Divider />
      </Box>

      {/* Content */}
      <Box sx={{ p: 1.5, overflow: "auto", height: "100%" }}>
        {/* Skeleton */}
        {loading && (
          <>
            <Skeleton variant="text" width={120} height={22} sx={{ mb: 1 }} />
            <SkeletonItem />
            <SkeletonItem />
            <SkeletonItem />
          </>
        )}

        {!loading &&
          Array.isArray(notifications) &&
          notifications.length === 0 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 3,
                border: isDark ? "1px dashed rgba(255,255,255,0.14)" : "1px dashed rgba(0,0,0,0.18)",
                background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.86)",
              }}
            >
              <Typography sx={{ fontWeight: 800 }}>
                No tenés notificaciones
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Cuando ocurra algo importante en el sistema, lo vas a ver acá.
              </Typography>
            </Box>
          )}

        {!loading &&
          grouped.map((g) => (
            <Box key={g.label} sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 0.5,
                  mb: 1,
                }}
              >
                <Typography
                  sx={{ fontWeight: 900, fontSize: 13.5, opacity: 0.85 }}
                >
                  {g.label}
                </Typography>

                <Typography variant="caption" sx={{ opacity: 0.6 }}>
                  {g.items.length} {g.items.length === 1 ? "evento" : "eventos"}
                </Typography>
              </Box>

              {g.items.map((n) => {
                const unread = !n.read_at;
                const date = n.created_at;
                const fullDate = date ? new Date(date).toLocaleString() : "";
                const ago = timeAgo(date);
                const link = getNotificationLink(n);

                return (
                  <Box
                    key={n.id}
                    onClick={
                      link
                        ? () => {
                            navigate(link);
                            onClose();
                          }
                        : undefined
                    }
                    sx={{
                      mb: 1,
                      p: 1.25,
                      borderRadius: 3,
                      cursor: link ? "pointer" : "default",
                      border: unread
                        ? "1px solid rgba(25,118,210,0.22)"
                        : isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                      background: unread
                        ? "rgba(25,118,210,0.06)"
                        : isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.86)",
                      transition: "all .18s ease",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: "0 12px 30px rgba(0,0,0,0.08)",
                        background: unread
                          ? "rgba(25,118,210,0.09)"
                          : isDark ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.86)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.25,
                        alignItems: "flex-start",
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: 3,
                          bgcolor: unread ? "primary.main" : "grey.200",
                          color: unread ? "white" : "grey.700",
                          flexShrink: 0,
                        }}
                      >
                        <NotificationsIcon fontSize="small" />
                      </Avatar>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: unread ? 900 : 700,
                              fontSize: 14.5,
                              lineHeight: 1.2,
                              wordBreak: "break-word",
                            }}
                          >
                            {n.data?.message ?? "Notificación"}
                          </Typography>

                          {unread && (
                            <Box
                              sx={{
                                mt: 0.6,
                                width: 8,
                                height: 8,
                                borderRadius: 999,
                                bgcolor: "primary.main",
                                flexShrink: 0,
                              }}
                            />
                          )}
                        </Box>

                        <Box
                          sx={{
                            mt: 0.8,
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <Tooltip title={fullDate || ""}>
                            <Chip
                              size="small"
                              label={ago || fullDate || "—"}
                              sx={{
                                height: 22,
                                borderRadius: 999,
                                bgcolor: "rgba(0,0,0,0.04)",
                                "& .MuiChip-label": {
                                  px: 1,
                                  fontWeight: 700,
                                  fontSize: 12,
                                },
                              }}
                            />
                          </Tooltip>

                          {n.data?.type && (
                            <Chip
                              size="small"
                              label={getNotificationTypeLabel(n)}
                              sx={{
                                height: 22,
                                borderRadius: 999,
                                bgcolor: "rgba(25,118,210,0.10)",
                                color: "primary.main",
                                "& .MuiChip-label": {
                                  px: 1,
                                  fontWeight: 800,
                                  fontSize: 12,
                                },
                              }}
                            />
                          )}

                          {(n.data?.warning_details ?? n.data?.data?.warning_details)?.length > 0 && (
                            <Chip
                              size="small"
                              icon={<WarningAmberIcon sx={{ fontSize: "14px !important" }} />}
                              label={(() => {
                                const wd = n.data?.warning_details ?? n.data?.data?.warning_details;
                                const total = n.data?.warning_count ?? n.data?.data?.warning_count ?? wd.length;
                                return expandedId === n.id
                                  ? "Ocultar detalles"
                                  : `Ver ${total} problema${total !== 1 ? "s" : ""}`;
                              })()}
                              deleteIcon={
                                expandedId === n.id
                                  ? <ExpandLessIcon sx={{ fontSize: "15px !important" }} />
                                  : <ExpandMoreIcon sx={{ fontSize: "15px !important" }} />
                              }
                              onDelete={() => toggleExpand(n.id)}
                              onClick={() => toggleExpand(n.id)}
                              sx={{
                                height: 22,
                                borderRadius: 999,
                                cursor: "pointer",
                                bgcolor: "rgba(237,108,2,0.10)",
                                color: "warning.dark",
                                border: "1px solid rgba(237,108,2,0.25)",
                                "& .MuiChip-label": {
                                  px: 0.8,
                                  fontWeight: 800,
                                  fontSize: 11.5,
                                },
                              }}
                            />
                          )}

                          {(n.data?.job_history_id ?? n.data?.data?.job_history_id) && (
                            <Chip
                              size="small"
                              icon={<DownloadIcon sx={{ fontSize: "14px !important" }} />}
                              label={
                                downloadingId === (n.data?.job_history_id ?? n.data?.data?.job_history_id)
                                  ? "Descargando..."
                                  : "Reporte completo (CSV)"
                              }
                              onClick={() =>
                                handleDownloadReport(
                                  n.data?.job_history_id ?? n.data?.data?.job_history_id,
                                )
                              }
                              disabled={
                                downloadingId === (n.data?.job_history_id ?? n.data?.data?.job_history_id)
                              }
                              sx={{
                                height: 22,
                                borderRadius: 999,
                                cursor: "pointer",
                                bgcolor: "rgba(25,118,210,0.10)",
                                color: "primary.main",
                                border: "1px solid rgba(25,118,210,0.25)",
                                "& .MuiChip-label": {
                                  px: 0.8,
                                  fontWeight: 800,
                                  fontSize: 11.5,
                                },
                              }}
                            />
                          )}
                        </Box>

                        {(n.data?.warning_details ?? n.data?.data?.warning_details)?.length > 0 && (
                          <Collapse in={expandedId === n.id} timeout="auto" unmountOnExit>
                            <WarningDetails
                              details={n.data?.warning_details ?? n.data?.data?.warning_details}
                              isDark={isDark}
                              onRequestSession={setSessionRequiredLawyer}
                            />
                            {(n.data?.warning_details_truncated ?? n.data?.data?.warning_details_truncated) && (
                              <Typography
                                variant="caption"
                                sx={{ display: "block", mt: 0.6, px: 0.3, opacity: 0.7, fontStyle: "italic" }}
                              >
                                Mostrando las primeras {(n.data?.warning_details ?? n.data?.data?.warning_details).length} de{" "}
                                {n.data?.warning_count ?? n.data?.data?.warning_count}. Descargá el reporte
                                completo (CSV) para ver el resto.
                              </Typography>
                            )}
                          </Collapse>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}
      </Box>
    </Drawer>
    </>
  );
}
